<template>
  <div v-click-outside="activeOff">
    <button
      class="search__icon"
      :aria-expanded="searchOpen"
      aria-label="open website search"
      :class="{ 'toggle--open': searchOpen }"
      @click="toggleActive"
    >
      <font-awesome-icon icon="search" />
    </button>
    <div
      class="search__container"
      :class="{ 'toggle--open': searchOpen }"
      :style="[!searchOpen ? { visibility: 'hidden' } : '']"
      tabindex="0"
      @keydown.esc="activeOff"
    >
      <AisInstantSearch
        :search-client="searchClient"
        :index-name="algoliaIndexName"
        :middlewares="middlewares"
      >
        <div class="search-box">
          <AisSearchBox>
            <template #default="{ refine }">
              <input
                v-model="query"
                type="search"
                :placeholder="searchBarLabel"
                class="ais-SearchBox-input"
                @input="refine($event.currentTarget.value)"
              />
            </template>
          </AisSearchBox>
          <button
            class="search-box__close"
            aria-label="close website search"
            :tabindex="!searchOpen ? -1 : 0"
            :aria-hidden="!searchOpen"
            @click="activeOff"
          >
            <img
              src="/themes/custom/ncea/dist/images/icon_close_white.svg"
              alt=""
              class="search-box__close-icon"
            />
          </button>
        </div>
        <div class="homepage__advanced-search homepage__advanced-search--overlay">
          <a
            class="homepage__advanced-search-link homepage__advanced-search-link--overlay text-tiny"
            :href="showMoreURL"
          >
            {{ trans('Advanced Search', !isTeReo) }}
          </a>
        </div>
        <SiteSearchResults :is-te-reo="isTeReo" />
        <div class="search__show-more">
          <MoeButton
            :label="trans('View all results', !isTeReo)"
            theme="secondary"
            :url="showMoreURL"
            rounded
          />
        </div>
        <AisConfigure filters="published:true AND NOT is_orphan:true" />
      </AisInstantSearch>
    </div>
    <div
      class="search__background"
      :class="{ 'toggle--open': searchOpen }"
      @click="activeOff"
    />
  </div>
</template>

<script>
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares';
import aa from 'search-insights';
import ClickOutside from 'vue-click-outside';
import { AisConfigure, AisInstantSearch, AisSearchBox } from 'vue-instantsearch/vue3/es';
import { searchClient, trans } from '../../utils/utils';
import MoeButton from '../MoeButton/MoeButton.vue';
import SiteSearchResults from './SiteSearchResults.vue';

const insightsMiddleware = createInsightsMiddleware({
  insightsClient: aa,
  insightsInitParams: {
    useCookie: true,
  },
});

export default {
  name: 'SiteSearch',
  components: {
    AisInstantSearch,
    SiteSearchResults,
    AisSearchBox,
    AisConfigure,
    MoeButton,
  },
  directives: {
    ClickOutside,
  },
  props: {
    algoliaIndexName: {
      type: String,
      required: true,
    },
    isTeReo: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      active: false,
      indexName: this.algoliaIndexName,
      middlewares: [insightsMiddleware],
      searchClient,
      query: '',
      searchOpen: false,
      inputEl: null,
      initialUiState: {
        [this.algoliaIndexName]: {
          configure: {
            disjunctiveFacetsRefinements: {
              published: true,
              is_orphan: false,
            },
          },
        },
      },
    };
  },
  computed: {
    showMoreURL() {
      const miUrlPart = this.isTeReo ? '/mi' : '';
      const queryPart = this.query ? `?query=${this.query}` : '';
      return `${miUrlPart}/search${queryPart}`;
    },
    searchBarLabel() {
      return this.isTeReo
        ? 'Rapua he marau, he paerewa, he ngohe, he rauemi matihiko rānei'
        : 'Search subjects, standards, activities and media';
    },
  },
  methods: {
    toggleActive() {
      this.query = '';
      this.searchOpen = !this.searchOpen;
      document.body.classList.toggle('overflow-hidden-search');
      setTimeout(() => document.querySelector('.ais-SearchBox-input').focus(), 500);
    },
    activeOff() {
      this.searchOpen = false;
      this.query = '';
      document.body.classList.toggle('overflow-hidden-search');
      setTimeout(() => document.querySelector('.ais-SearchBox-input').blur(), 500);
    },
    trans,
  },
};
</script>

<style lang="scss">
.search {
  display: flex;

  &__toggle {
    display: flex;
  }

  &__container {
    position: fixed;
    left: 100%;
    transition: all $transition-duration-x-short ease-out;
    height: 100vh;
    background-color: $color-white;
    z-index: 33;
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    opacity: 0;

    body.user-logged-in & {
      height: calc(100vh - 7rem);
    }

    @include breakpoint-sm-up {
      width: 75%;
    }

    &.toggle--open {
      top: 0;
      left: 0;
      opacity: 1;

      body.user-logged-in & {
        top: 5.5rem;
      }

      @include breakpoint-sm-up {
        left: 25%;
      }
    }

    .ais-InstantSearch {
      padding-bottom: 4rem;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    /* stylelint-disable-next-line unit-disallowed-list */
    width: 19px;
    cursor: pointer;
    transition: all $transition-duration-short ease-out;
    background-color: $color-white;
    border: none;
    color: $color-blue-s3;

    @include hover-focus-active {
      color: $color-blue-button-dark;

      @include breakpoint-tablet-down {
        color: $color-blue-button-dark;
      }
    }

    /* stylelint-disable-next-line unit-disallowed-list */
    @media only screen and (max-width: 1250px) {
      border: none;
    }

    &.toggle--open {
      background-color: $color-white;
      color: $color-white;
    }

    .fa-magnifying-glass {
      font-size: $font-size-small-medium;
    }
  }

  &__show-more {
    display: grid;
    justify-items: center;
    margin-top: 3rem;
  }

  &__background {
    width: 100%;
    opacity: 0;
    left: 0;
    top: 0;
    transition: all $transition-duration-x-short ease-out;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.8);
    position: fixed;
    z-index: 32;
    pointer-events: none;

    &.toggle--open {
      pointer-events: auto;
      opacity: 1;
    }
  }
}

.search-box {
  position: sticky;
  display: grid;
  top: 0;
  z-index: 1;

  .ais-SearchBox {
    grid-area: 1/-1;

    &-submit,
    &-reset {
      display: none;
    }

    &-input {
      @include text-standard;

      width: 100%;
      height: $size-header-height;
      box-sizing: border-box;
      border: none;
      background-color: $color-blue-s3;
      color: $color-white;
      font-weight: $font-weight-bold;
      padding: 1.5rem 0 1.5rem 3rem;
      // Remove input field rounding on Safari.
      // Ref: https://stackoverflow.com/questions/2918707/turn-off-iphone-safari-input-element-rounding
      border-radius: 0;
      -webkit-appearance: none;

      &::placeholder {
        color: $color-white;
        font-weight: $font-weight-light;
      }

      &:focus {
        border: none;
        outline: none;
      }

      @include breakpoint-tablet-down {
        padding: 1.5rem;
      }
    }
  }

  &__close {
    grid-area: 1/-1;
    justify-self: flex-end;
    cursor: pointer;
    background-color: $color-blue-s3;
    border: none;
    padding: 0 1.5rem;
    margin: 0;

    @include hover-focus-active {
      .search-box__close-icon {
        opacity: 1;
      }
    }
  }

  &__close-icon {
    opacity: 0.75;
    transition: $transition-all;
  }
}
</style>
